import React, { useState, useRef, useEffect } from 'react'
import styled from 'styled-components/macro'
import axios from 'axios'
import ReactGA from 'react-ga'

import Logo from '../../components/Logo/Logo'
import Body from '../../components/Typography/Body'
import Button from '../../components/Button/Button'
import envelope from './envelope.svg'
import { testUrl } from '../../data/urls'
import data from './screener-questions'
import CheckBox from '../../components/Button/CheckBox'
import RegularOutside from '../../components/Input/RegularOutside'
import {
  Header,
  HeaderWrapper,
  Hero,
  Content,
  HeaderTitle,
  EmailSection,
  EmailWrapper
} from '../Shared/Components'
import Confirmation from './Confirmation'
import Check from '../../components/Check/Check'
import Footer from '../../components/Footer/Footer'

const Wrapper = styled.div`
  position: relative;
`

const HeroTitle = styled.h1`
  font-family: 'Pluto Sans', sans-serif;
  font-weight: normal;
  font-size: 32px;
  line-height: 40px;
  color: ${(props) => props.theme.colors.grey.white};
  text-align: center;
  margin-bottom: 24px;

  @media only screen and (min-width: 768px) {
    font-size: 48px;
    line-height: 56px;
    margin-bottom: 40px;
    max-width: 820px;
  }
`

const HeroSubtitle = styled.h3`
  font-family: 'Pluto Sans', sans-serif;
  font-weight: normal;
  font-size: 16px;
  line-height: 24px;
  color: ${(props) => props.theme.colors.grey.white};
  text-align: center;

  @media only screen and (min-width: 768px) {
    font-size: 24px;
    line-height: 32px;
    max-width: 820px;
  }
`

const TextWrapper = styled.div`
  margin-bottom: 48px;

  @media only screen and (min-width: 768px) {
    min-width: 680px;
    max-width: 680px;
  }
`

const Answers = styled.div`
  & > button:not(:last-child) {
    margin-bottom: 16px;
  }
`

const Names = styled.div`
  & > div {
    margin-bottom: 16px;
  }

  @media only screen and (min-width: 768px) {
    display: flex;
    & > div:not(:last-of-type) {
      margin-right: 24px;
    }
  }
`

const CheckWrapper = styled.div`
  margin-bottom: 24px;
  display: flex;

  &:hover {
    cursor: pointer;
  }
`

const Screener = ({ location, demo }) => {
  const [answers, setAnswers] = useState({
    screeners: [],
    places: []
  })
  const [confirmation, setConfirmation] = useState(false)
  const [email, setEmail] = useState('')
  const [emailError, setEmailError] = useState(false)
  const [firstName, setFirstName] = useState('')
  const [firstNameError, setFirstNameError] = useState(false)
  const [lastName, setLastName] = useState('')
  const [lastNameError, setLastNameError] = useState('')
  const [agree, setAgree] = useState(false)

  useEffect(() => {
    ReactGA.pageview(window.location.pathname + window.location.search)
  }, [])

  const handleSubmit = async () => {
    const emailRegex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/

    const { screeners, places } = answers

    if (firstName.length < 1) {
      setFirstNameError('Please check your first name')
    } else if (lastName.length < 1) {
      setLastNameError('Please check your last name')
    } else if (email.length < 1 || !emailRegex.test(email)) {
      setEmailError('Please check your email')
    } else if (screeners.length < 1) {
      setEmailError('Please provide screeners')
    } else if (places.length < 1) {
      setEmailError('Please check how you got it')
    } else if (!agree) {
      setEmailError('Please declare that information is accurate')
    } else {
      try {
        await axios.post(`${testUrl}/screeners`, {
          email,
          firstName,
          lastName,
          screeners,
          places
        })

        setConfirmation(true)
      } catch (error) {
        console.log(error)
      }
      setAgree(false)
      setEmail('')
      setFirstName('')
      setLastName('')
    }
  }

  const handleAnswerClick = (value, apiName) => () => {
    // copy new answers and use new values on current group id
    const newAnswers = { ...answers }

    if (newAnswers[apiName].includes(value)) {
      // value is here, filter it out
      newAnswers[apiName] = newAnswers[apiName].filter((item) => item !== value)
    } else {
      // value is not here, add it
      newAnswers[apiName] = [...answers[apiName], value]
    }

    // set answers
    setAnswers(newAnswers)
    setEmailError(false)
  }

  const handleAgree = () => {
    setAgree(!agree)
    setEmailError(false)
  }

  const emailSectionRef = useRef(null)

  // const scrollToEmail = () => {
  //   if (emailSectionRef && emailSectionRef.current) {
  //     emailSectionRef.current.scrollIntoView({
  //       behavior: 'smooth',
  //       block: 'start'
  //     })
  //   }
  // }

  if (confirmation) {
    return <Confirmation />
  } else {
    return (
      <Wrapper>
        <Header>
          <HeaderWrapper>
            <Logo version='full' />
            {!demo && <Logo version='cbus' ml={5} />}
          </HeaderWrapper>
        </Header>
        <Hero demo={demo}>
          <HeroTitle>
            Tell us which health check you completed to enter the draw
          </HeroTitle>
          <HeroSubtitle>
            By giving us some small details of the check you completed,
            <br /> you will be eligible to enter for the change to win $5000
            into your super.
          </HeroSubtitle>
        </Hero>
        <Content>
          {data.map((item) => (
            <TextWrapper key={item.apiName}>
              <HeaderTitle>{item.title}</HeaderTitle>
              <Body fade mb={8}>
                {item.info}
              </Body>
              <Answers>
                {item.answers.map((answer) => (
                  <CheckBox
                    key={answer.value}
                    title={answer.title}
                    value={answer.value}
                    onClick={handleAnswerClick}
                    apiName={item.apiName}
                    checked={answers[item.apiName].includes(answer.value)}
                    enabled
                  />
                ))}
              </Answers>
            </TextWrapper>
          ))}
          <EmailSection ref={emailSectionRef}>
            <TextWrapper>
              <HeaderTitle>
                Enter your details to qualify for the competition draw.
              </HeaderTitle>
              <Body fade>
                Vivamus rhoncus turpis ex, at semper nisl vehicula eget. Integer
                ornare maximus nisl vitae rutrum. Sed in purus sit amet ante
                lobortis tincidunt. Mauris quis maximus magna. Aliquam eget ex
                eleifend diam iaculis imperdiet id vitae risus. Proin vitae
                congue orci, convallis rhoncus lacus.
              </Body>
              <EmailWrapper>
                <Names>
                  <RegularOutside
                    placeholder='First Name'
                    noButton
                    value={firstName}
                    setValue={setFirstName}
                    error={firstNameError}
                    setError={setFirstNameError}
                  />
                  <RegularOutside
                    placeholder='Last Name'
                    noButton
                    value={lastName}
                    setValue={setLastName}
                    error={lastNameError}
                    setError={setLastNameError}
                  />
                </Names>
                <RegularOutside
                  mb={2}
                  placeholder='steve.jobs@apple.com'
                  noButton
                  value={email}
                  setValue={setEmail}
                  error={emailError}
                  setError={setEmailError}
                />
                <CheckWrapper>
                  <Check
                    checked={agree}
                    onClick={handleAgree}
                    text='I declare the information I have provided here is accurate.'
                  />
                </CheckWrapper>
                <Button
                  error={firstNameError || lastNameError || emailError ? 1 : 0}
                  title='Submit'
                  onClick={handleSubmit}
                  icon={envelope}
                  showOnMobile
                />
              </EmailWrapper>
            </TextWrapper>
          </EmailSection>
        </Content>
        <Footer demo={demo} />
      </Wrapper>
    )
  }
}

export default Screener
